(function () {
  const STORAGE_KEY = "rj_welcome_popup";
  const PARAM_FORCE = "welcome_popup";
  const PARAM_RESET = "welcome_popup_reset";

  function getSearchParams() {
    return new URLSearchParams(window.location.search);
  }

  function isForceOpen() {
    const value = getSearchParams().get(PARAM_FORCE);
    return value === "1" || value === "true";
  }

  function isCustomerPosted() {
    return getSearchParams().get("customer_posted") === "true";
  }

  function isThemeCustomizer() {
    return Boolean(window.Shopify?.designMode && window.self !== window.top);
  }

  function shouldSuppressAutoOpen(storageKey) {
    if (isForceOpen()) return false;
    if (isThemeCustomizer()) return true;

    const status = localStorage.getItem(storageKey);
    return status === "subscribed" || status === "dismissed";
  }

  class WelcomePopup extends HTMLElement {
    constructor() {
      super();
      this.storageKey = this.dataset.storageKey || STORAGE_KEY;
      this.modal = this.querySelector("[data-welcome-modal]");
      this.form = this.querySelector("[data-welcome-form-panel] form");
      this.formPanel = this.querySelector("[data-welcome-form-panel]");
      this.successPanel = this.querySelector("[data-welcome-success]");
      this.errorEl = this.querySelector("[data-welcome-error]");
      this.errorText = this.querySelector("[data-welcome-error-text]");
      this.liquidError = this.querySelector("[data-welcome-liquid-error]");
      this.emailInput = this.querySelector("[data-welcome-email]");
      this.submitButton = this.querySelector("[data-welcome-submit]");
      this.copyButton = this.querySelector("[data-welcome-copy]");
      this.copyFeedback = this.querySelector("[data-welcome-copy-feedback]");
      this.codeEl = this.querySelector("[data-welcome-code]");
      this.shopNowLink = this.querySelector("[data-welcome-shop-now]");
      this.openTimeout = null;
      this.previousBodyOverflow = "";
    }

    connectedCallback() {
      if (!this.modal || !this.form) return;
      if (this.dataset.customerLoggedIn === "true") return;

      if (getSearchParams().has(PARAM_RESET)) {
        localStorage.removeItem(this.storageKey);
      }

      if (isForceOpen()) {
        localStorage.removeItem(this.storageKey);
      }

      this.bindEvents();

      const postedSuccessfully = Boolean(
        isCustomerPosted() ||
        this.querySelector("[data-welcome-liquid-success]") ||
        (this.successPanel && !this.successPanel.hidden)
      );

      if (postedSuccessfully) {
        this.showSuccess();
        this.open(true);
        // Clean up query param so back/forward navigation doesn't re-trigger
        if (isCustomerPosted() && window.history?.replaceState) {
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete("customer_posted");
          window.history.replaceState(null, "", cleanUrl.toString());
        }
        return;
      }

      if (this.liquidError) {
        this.open();
        return;
      }

      if (
        !isForceOpen() &&
        this.dataset.showMobile === "false" &&
        window.innerWidth < 750
      ) {
        return;
      }

      if (shouldSuppressAutoOpen(this.storageKey)) return;

      const delaySeconds = parseInt(this.dataset.delay, 10);
      const delay = isForceOpen()
        ? 0
        : Number.isNaN(delaySeconds)
          ? 4000
          : delaySeconds * 1000;

      this.openTimeout = window.setTimeout(() => this.open(), delay);
    }

    disconnectedCallback() {
      if (this.openTimeout) window.clearTimeout(this.openTimeout);
    }

    bindEvents() {
      this.form.addEventListener("submit", (event) => this.onSubmit(event));

      this.querySelectorAll("[data-welcome-dismiss]").forEach((button) => {
        button.addEventListener("click", () => this.dismiss());
      });

      this.modal.addEventListener("click", (event) => {
        if (event.target === this.modal || event.target.hasAttribute("data-welcome-backdrop")) {
          this.dismiss();
        }
      });

      document.addEventListener("keydown", this.onKeydown);

      if (this.copyButton) {
        this.copyButton.addEventListener("click", () => this.onCopyCode());
      }

      if (this.shopNowLink) {
        this.shopNowLink.addEventListener("click", (event) => this.onShopNow(event));
      }
    }

    onKeydown = (event) => {
      if (event.code !== "Escape" || !this.modal?.classList.contains("is-open")) return;
      this.dismiss();
    };

    open(force = false) {
      if (!this.modal) return;
      if (!force && !isForceOpen() && localStorage.getItem(this.storageKey) === "subscribed") return;

      this.modal.classList.add("is-open");
      this.modal.setAttribute("aria-hidden", "false");
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      const focusTarget =
        this.emailInput || this.modal.querySelector('[role="dialog"]');
      if (focusTarget && this.formPanel && !this.formPanel.hidden) {
        window.requestAnimationFrame(() => focusTarget.focus());
      }
    }

    dismiss() {
      if (!isForceOpen()) {
        const status = localStorage.getItem(this.storageKey);
        if (status !== "subscribed") {
          localStorage.setItem(this.storageKey, "dismissed");
        }
      }
      this.closeModal();
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove("is-open");
      this.modal.setAttribute("aria-hidden", "true");
      document.body.style.overflow = this.previousBodyOverflow || "";
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;
      if (isLoading) {
        this.submitButton.disabled = true;
        this.submitButton.classList.add("loading");
      } else {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove("loading");
      }
    }

    showError(message) {
      if (!this.errorEl || !this.errorText) return;
      this.errorText.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      if (this.errorText) this.errorText.textContent = "";
    }

    onShopNow(event) {
      event.preventDefault();
      localStorage.setItem(this.storageKey, "subscribed");

      const code = this.dataset.discountCode?.trim();
      if (code) {
        fetch(`/discount/${encodeURIComponent(code)}`, {
          credentials: "same-origin",
          redirect: "follow",
        }).catch(() => {});
      }

      this.closeModal();
    }

    showSuccess() {
      const code = this.dataset.discountCode || "";
      if (this.codeEl) this.codeEl.textContent = code;
      if (this.formPanel) this.formPanel.hidden = true;
      if (this.successPanel) this.successPanel.hidden = false;
      this.hideError();
      this.setLoading(false);
      // Mark subscribed after panels are updated so open()'s guard doesn't fire first
      localStorage.setItem(this.storageKey, "subscribed");
    }

    onSubmit(event) {
      this.hideError();

      const email = this.emailInput?.value?.trim();
      if (!email) {
        event.preventDefault();
        this.showError("Please enter a valid email address.");
        return;
      }

      this.setLoading(true);
      // Allow native form POST to proceed
    }

    async onCopyCode() {
      const code = this.dataset.discountCode || this.codeEl?.textContent || "";
      if (!code) return;

      try {
        await navigator.clipboard.writeText(code);
        if (this.copyFeedback) {
          this.copyFeedback.hidden = false;
          window.setTimeout(() => {
            if (this.copyFeedback) this.copyFeedback.hidden = true;
          }, 2500);
        }
      } catch {
        /* clipboard unavailable */
      }
    }
  }

  if (!customElements.get("welcome-popup")) {
    customElements.define("welcome-popup", WelcomePopup);
  }
})();
